import { FC, useEffect, useState } from 'react';
import { format } from 'date-fns';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { IconProgressCircle } from '../assets/icons';
import { AudioType, getAppAudio } from '../utils';

const Wrapper = styled.div`
  width: 70px;
  height: 70px;
  position: relative;
  border-radius: 50%;
`;

const Label = styled(Typography)`
  position: absolute;
  inset: 9px;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  background: white;
`;

const incomingChatInitialAudio = getAppAudio(AudioType.IncomingChatInitial);
const incomingChatEverySecondAudio = getAppAudio(
  AudioType.IncomingChatEverySecond,
);

type Props = {
  initialDurationSeconds: number;
  remainingDurationSeconds: number;
  isPaused?: boolean;
  onTimeout: () => void;
  isMuted?: boolean;
};

const CountdownTimer: FC<Props> = ({
  initialDurationSeconds,
  remainingDurationSeconds: remainingDurationSecondsProp,
  onTimeout,
  isPaused = false,
  isMuted = false,
}) => {
  const [remainingDurationSeconds, setRemainingDurationSeconds] =
    useState<number>(remainingDurationSecondsProp);

  useEffect(() => {
    if (!isMuted) incomingChatInitialAudio.play();
  }, [isMuted]);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setRemainingDurationSeconds((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(interval);
          onTimeout();
          return 0;
        }

        if (!isMuted) incomingChatEverySecondAudio.play();

        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isMuted, isPaused, onTimeout]);

  return (
    <Wrapper>
      <IconProgressCircle
        initialDurationSeconds={initialDurationSeconds}
        remainingDurationSeconds={remainingDurationSeconds}
        animationDisabled={isPaused}
      />
      <Label>{format(remainingDurationSeconds * 1000, 'mm:ss')}</Label>
    </Wrapper>
  );
};

export default CountdownTimer;
